import React, {
  useEffect,
  useMemo,
} from 'react';
import BaseDialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  AppointmentTypeSettingDTO,
} from 'dtos/appointmentTypeSettingDTO';
import isNil from 'lodash/isNil';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Button from 'pages/Dashboard/components/Button';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import {
  useGetSettingsGetAppointmentSettings,
  useUpsertAppointmentType,
} from 'views/EMR/Settings/hooks';
import DurationTextField from 'views/EMR/Settings/Appointments/DurationTextField';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import head from 'lodash/head';
import IntakeQustionnaireType from 'views/EMR/Settings/Appointments/IntakeQustionnaireType';

export type AppointmentTypeDialogParams = {
  data?: AppointmentTypeSettingDTO;
};

type Props = AppointmentTypeDialogParams & {
  onClose: () => void;
};

export default function Dialog({
  data,
  onClose,
}: Props) {
  const isNew = isNil(data);
  const { data: appointmentSettings } = useGetSettingsGetAppointmentSettings();
  const { intakeQuestionnaireOptions = [] } = appointmentSettings ?? {};

  const { mutateAsync: upsertAppointmentType } = useUpsertAppointmentType(isNew);

  const formProps = useForm<AppointmentTypeSettingDTO>({
    mode: 'onChange',
    defaultValues: data ?? undefined,
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid, isSubmitting },
  } = formProps;

  useEffect(() => {
    if (isNew && !isNil(intakeQuestionnaireOptions)) {
      setValue('intakeQuestionnaireType', head(intakeQuestionnaireOptions)?.value);
    }
  }, [intakeQuestionnaireOptions, isNew]);

  const onCreateOrUpdate = async (data: AppointmentTypeSettingDTO) => {
    try {
      await upsertAppointmentType(data);
      settingsEventChannel.emit('onAppointmentTypesChange');
      onClose?.();
    } catch (e) {
      console.error(e);
    }
  };

  const submitButtonText = useMemo(() => getSubmitButtonText(
    isNew,
    isSubmitting,
  ), [isNew, isSubmitting]);

  return (
    <BaseDialog
      title={(
        <div className="flex flex-1 flex-col space-y-2">
          <h2 className="text-lg font-semibold">
            {`${isNew ? 'Create' : 'Edit'} Appointment Type`}
          </h2>
        </div>
      )}
      closeBtnClasses="!-top-1"
      onClose={onClose}
      onConfirm={noop}
      className="!w-[28rem] populate-form"
      maxWidth="lg"
      open
    >
      <div className="flex flex-col gap-form-row">
        <FormProvider {...formProps}>
          <div className="flex flex-col gap-form-row">
            <Controller
              name="appointmentTypeName"
              control={control}
              rules={{ required: 'Appointment Type Name is required.' }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  className="text-gray-500 text-xs"
                  id="appointment-type"
                  error={invalid}
                  showErrorText={false}
                  label="Type of Visit"
                  placeholder="E.g. Follow Up"
                  inputProps={{ maxLength: 64 }}
                  required
                />
              )}
            />
            <Controller
              name="intakeQuestionnaireType"
              control={control}
              rules={{ required: 'Intake Questionnaire Type is required.' }}
              render={({ field, fieldState: { invalid } }) => (
                <Wrapper
                  label="Intake Questionnaire Type"
                  id="intakeQuestionnaireType"
                  required
                  error={invalid}
                >
                  <IntakeQustionnaireType
                    {...field}
                    value={field.value ?? ''}
                    options={intakeQuestionnaireOptions ?? []}
                    isLoading={isSubmitting}
                    onClose={onClose}
                    onSameValueSelected={onClose}
                  />
                </Wrapper>
              )}
            />
            <Controller
              name="defaultDurationInMinutes"
              control={control}
              rules={{ required: 'Duration is required.' }}
              render={({
                field,
                fieldState: { error },
              }) => (
                <Wrapper
                  id="defaultDurationInMinutes-wrapper"
                  error={!isNil(error)}
                  showErrorText={false}
                  label="Duration"
                  required
                >
                  <DurationTextField
                    {...field}
                    onChange={(value?: string | number | null) => {
                      field.onChange(value);
                    }}
                    placeholder="E.g. 30 minutes"
                  />
                </Wrapper>
              )}
            />
          </div>
          <div className="flex flex-row-reverse justify-between gap-3">
            <DisabledFormTooltip>
              <Button
                className="!py-1.5 ml-3"
                variant="contained"
                type="submit"
                color="primary"
                onClick={handleSubmit(onCreateOrUpdate)}
                disabled={!isValid || isSubmitting}
              >
                {submitButtonText}
              </Button>
            </DisabledFormTooltip>
            <Button
              className="!py-1.5 ml-3"
              variant="outlined"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </div>
        </FormProvider>
      </div>
    </BaseDialog>
  );
}
